// src/components/clients/verificationReadout.ts
//
// Pure function that converts a lead's verification_flags into a unified,
// ordered list of "verdict-first" check rows the popover renders. Every
// verification signal maps to exactly one CheckRow. New vendors / signals
// become one new branch here; the UI doesn't need to change.
//
// Design note: this deliberately returns an empty array for pre-verify
// leads (no vendor provider stamped yet). Callers render an informational
// card instead — no fake verdicts before real data.

import type { Lead } from "./types";

export type CheckTone = "pass" | "warn" | "fail" | "neutral" | "skipped";
export type CheckCategory = "phone" | "email" | "identity" | "fraud";

export interface CheckRow {
  id: string;
  category: CheckCategory;
  headline: string;
  detail: string;
  tone: CheckTone;
  value?: string; // optional pill — "mobile", "Verizon", etc.
}

export const TONE_COLOR: Record<CheckTone, string> = {
  pass: "#10B981",
  warn: "#F59E0B",
  fail: "#EF4444",
  neutral: "#64748B",
  skipped: "#94A3B8",
};

function identityMatchRow(flags: Record<string, any>): CheckRow {
  const summary = flags["identity_match_summary"];
  if (summary === "exact") {
    return {
      id: "identity_match",
      category: "identity",
      tone: "pass",
      headline: "Identity matches carrier billing",
      detail:
        "First and last name on the lead both appear on the phone's carrier billing record — the strongest single name-to-phone signal.",
    };
  }
  if (summary === "partial") {
    return {
      id: "identity_match",
      category: "identity",
      tone: "warn",
      headline: "Partial identity match",
      detail:
        "One name token matches the carrier billing record — could be a nickname, name change, or shared family phone.",
    };
  }
  if (summary === "mismatch") {
    return {
      id: "identity_match",
      category: "identity",
      tone: "fail",
      headline: "Name doesn't match billing record",
      detail:
        "Neither first nor last name on the lead appears on the carrier's billing record. Borrowed phone is possible, but paired with a CNAM mismatch this is a strong fraud signal.",
    };
  }
  return {
    id: "identity_match",
    category: "identity",
    tone: "skipped",
    headline: "Identity Match unavailable",
    detail:
      "Couldn't run the billing-record check — regional gap, prepaid account, or Twilio error. Other signals still counted.",
  };
}

function cnamRow(flags: Record<string, any>): CheckRow {
  const match = flags["caller_name_match"];
  const cnamName = flags["caller_name_raw"] as string | undefined;

  if (match === "exact") {
    return {
      id: "cnam",
      category: "identity",
      tone: "pass",
      headline: cnamName ? `Registered to ${cnamName}` : "Caller ID matches",
      detail:
        "Caller ID registration matches the lead's name — works best on landlines and business numbers.",
    };
  }
  if (match === "partial") {
    return {
      id: "cnam",
      category: "identity",
      tone: "warn",
      headline: cnamName
        ? `Registered to ${cnamName} (partial)`
        : "Partial Caller ID match",
      detail:
        "One name token overlaps with the Caller ID record — possibly a middle name or spouse.",
    };
  }
  if (match === "mismatch") {
    return {
      id: "cnam",
      category: "identity",
      tone: "fail",
      headline: cnamName
        ? `Registered to ${cnamName}`
        : "Caller ID doesn't match",
      detail:
        "Caller ID says this phone belongs to a different name. Could be a shared phone — worth a double-check.",
    };
  }
  return {
    id: "cnam",
    category: "identity",
    tone: "skipped",
    headline: "Caller ID name not published",
    detail:
      "Carrier didn't publish a subscriber name — common on mobile and not a red flag on its own.",
  };
}

function phoneValidityRow(flags: Record<string, any>): CheckRow {
  const valid = flags["phone_valid"];
  const lineType = flags["phone_line_type"] as string | undefined;

  if (valid === false) {
    return {
      id: "phone_valid",
      category: "phone",
      tone: "fail",
      headline: "Phone number not valid",
      detail:
        "Twilio doesn't recognize this number in any carrier's routing table — likely fake, typo'd, or disconnected.",
    };
  }
  if (valid !== true) {
    return {
      id: "phone_valid",
      category: "phone",
      tone: "skipped",
      headline: "Phone validity unavailable",
      detail: "Couldn't confirm whether the number is active.",
    };
  }
  if (lineType === "voip") {
    return {
      id: "phone_valid",
      category: "phone",
      tone: "warn",
      headline: "Valid VOIP number",
      detail:
        "Number is active but routed through a VOIP provider. VOIP numbers are cheap to create anonymously — common in spam leads.",
      value: "VOIP",
    };
  }
  if (lineType === "mobile") {
    return {
      id: "phone_valid",
      category: "phone",
      tone: "pass",
      headline: "Valid mobile number",
      detail:
        "Carrier confirmed the number is active and reaches a mobile device.",
      value: "mobile",
    };
  }
  if (lineType === "landline" || lineType === "tollfree") {
    return {
      id: "phone_valid",
      category: "phone",
      tone: "pass",
      headline: `Valid ${lineType} number`,
      detail: "Carrier confirmed the number is active.",
      value: lineType,
    };
  }
  return {
    id: "phone_valid",
    category: "phone",
    tone: "pass",
    headline: "Valid phone number",
    detail: "Carrier confirmed the number is active.",
    value: lineType,
  };
}

function smsPumpingRow(flags: Record<string, any>): CheckRow {
  const tier = flags["sms_pumping_risk_tier"];
  const score = flags["sms_pumping_risk_score"] as number | undefined;
  const scoreSuffix =
    typeof score === "number" ? ` (score ${score}/100)` : "";

  if (tier === "carrier_block") {
    return {
      id: "sms_pumping",
      category: "fraud",
      tone: "fail",
      headline: "Carrier blocked for SMS fraud",
      detail:
        "A carrier has blocklisted this number for confirmed SMS pumping. Strong fraud signal — treat as spam.",
    };
  }
  if (tier === "high") {
    return {
      id: "sms_pumping",
      category: "fraud",
      tone: "fail",
      headline: `High SMS pumping risk${scoreSuffix}`,
      detail:
        "Twilio's fraud database rates this number high-risk. Likely part of a coordinated spam pattern.",
    };
  }
  if (tier === "medium") {
    return {
      id: "sms_pumping",
      category: "fraud",
      tone: "warn",
      headline: `Moderate SMS pumping risk${scoreSuffix}`,
      detail:
        "Some fraud-pattern overlap. Not a blocker on its own, but worth noting if other signals are off.",
    };
  }
  if (tier === "low") {
    return {
      id: "sms_pumping",
      category: "fraud",
      tone: "pass",
      headline: "No SMS pumping risk detected",
      detail:
        "Number isn't flagged in Twilio's coordinated-fraud database.",
    };
  }
  return {
    id: "sms_pumping",
    category: "fraud",
    tone: "skipped",
    headline: "Fraud check unavailable",
    detail: "Couldn't assess SMS pumping risk for this number.",
  };
}

function reassignedRow(flags: Record<string, any>): CheckRow | null {
  const recent = flags["reassigned_recently"];
  const date = flags["reassigned_last_verified_date"] as string | undefined;

  if (recent === true) {
    return {
      id: "reassigned",
      category: "fraud",
      tone: "warn",
      headline: "Phone recently changed owners",
      detail: date
        ? `Number verified to the current owner on ${date} — a mild churn / fraud signal.`
        : "Number verified to the current owner within the last 90 days — a mild churn / fraud signal.",
    };
  }
  if (recent === false) {
    return {
      id: "reassigned",
      category: "phone",
      tone: "pass",
      headline: "Phone has held its owner for months+",
      detail: "No recent reassignment detected — the current owner is established.",
    };
  }
  // Missing data — skip silently rather than add a noisy "unavailable" row
  // for an optional secondary signal.
  return null;
}

function emailDeliverableRow(flags: Record<string, any>, lead: VerificationSubject): CheckRow | null {
  const deliverable = flags["email_deliverable"];
  if (!lead.email) return null;

  if (deliverable === true) {
    return {
      id: "email_deliverable",
      category: "email",
      tone: "pass",
      headline: "Email domain can receive mail",
      detail:
        "DNS confirms the email's domain has working mail servers. Catches typos and defunct domains.",
    };
  }
  if (deliverable === false) {
    return {
      id: "email_deliverable",
      category: "email",
      tone: "fail",
      headline: "Email domain can't receive mail",
      detail:
        "DNS couldn't find mail servers for this domain. Likely a typo'd or defunct email.",
    };
  }
  return {
    id: "email_deliverable",
    category: "email",
    tone: "skipped",
    headline: "Email check unavailable",
    detail: "DNS lookup didn't complete for this domain.",
  };
}

function emailDisposableRow(flags: Record<string, any>, lead: VerificationSubject): CheckRow | null {
  const disposable = flags["email_disposable"];
  if (!lead.email) return null;

  if (disposable === true) {
    return {
      id: "email_disposable",
      category: "fraud",
      tone: "fail",
      headline: "Disposable email address",
      detail:
        "Domain is on our blocklist of burner providers (Mailinator, TempMail, etc.) — strong spam signal.",
    };
  }
  if (disposable === false) {
    return {
      id: "email_disposable",
      category: "email",
      tone: "pass",
      headline: "Not a disposable email",
      detail: "Domain isn't on the blocklist of known burner providers.",
    };
  }
  return null; // neutral absence — don't clutter the readout
}

function nameEmailRow(flags: Record<string, any>, lead: VerificationSubject): CheckRow | null {
  const match = flags["name_email_match"];
  if (match === null || match === undefined || !lead.email || !lead.name) {
    return null;
  }
  if (match === true) {
    return {
      id: "name_email",
      category: "email",
      tone: "pass",
      headline: "First name appears in email",
      detail:
        "Lead's first name shows up in the email's local part — modest alignment signal.",
    };
  }
  return {
    id: "name_email",
    category: "email",
    tone: "neutral",
    headline: "First name not in email",
    detail:
      "Weak signal — real emails often use initials or nicknames. Doesn't punish the score.",
  };
}

/**
 * Build the ordered readout. Returns [] for pre-verify leads; callers
 * should render an informational CTA card instead.
 *
 * Row order (by admin ROI when scanning a popover):
 *   1. Identity Match  (strongest name-to-phone signal, works on mobile)
 *   2. CNAM            (complements IM on landlines)
 *   3. Phone validity  (foundation signal — valid? what line type?)
 *   4. SMS Pumping     (fraud flag)
 *   5. Reassigned      (churn/fraud flag)
 *   6. Email domain    (deliverability)
 *   7. Email disposable (fraud flag)
 *   8. Name ↔ email     (weakest, last)
 */
/**
 * Everything buildReadout actually reads. Declared as a structural subset so
 * the LEAD modal — which holds a DashboardLeadRow, not a Lead — can render the
 * same evidence without a cast. A cast here would have been a claim that the
 * two types match, which is not something this file can know.
 */
export interface VerificationSubject {
  /** Both are guarded with `!lead.name` / `!lead.email` before use, so the
   *  looser nullability the dashboard row carries is already handled — this
   *  type states that rather than borrowing Lead's stricter one and casting. */
  name?: string | null;
  email?: string | null;
  verification_flags?: Record<string, unknown> | null;
  verification_provider?: string | null;
}

export function buildReadout(lead: VerificationSubject): CheckRow[] {
  const isVerified = Boolean((lead.verification_provider || "").trim().length);
  if (!isVerified) return [];

  const flags = lead.verification_flags || {};
  const rows: Array<CheckRow | null> = [
    identityMatchRow(flags),
    cnamRow(flags),
    phoneValidityRow(flags),
    smsPumpingRow(flags),
    reassignedRow(flags),
    emailDeliverableRow(flags, lead),
    emailDisposableRow(flags, lead),
    nameEmailRow(flags, lead),
  ];
  return rows.filter((r): r is CheckRow => r !== null);
}
